// Where a task modal lands, decided once when the modal opens. `null` means it opens at the top with
// the normal comment box - every way of opening a task except its Nova button. 'view' brings the
// most recent answer into view (View, for somebody who cannot act on Nova). 'reply' does that and
// opens the composer straight into replying to it (Ready and Continue).
export type NovaLandingKind = 'view' | 'reply' | null;

// The landing travels with the navigation that opens the modal, as route state on the `/T{id}`
// push, rather than in a slot beside it. A slot outlives the click that filled it: an opener that
// never mounts a task modal leaves it for some later, ordinary open of the task, and two quick
// clicks on different rows overwrite or consume each other's. Route state belongs to one history
// entry, so it can only ever reach the modal that entry opens.
const ROUTE_STATE_KEY = 'novaLanding';

// The kind the Nova button is opening with, set only for the synchronous call that opens the task.
// Every surface hands the button its own opener (onOpenTask), and every one of those ends in the
// shared showTaskModal helper in util.js, which reads this as it pushes the route. Held for the
// call alone, so nothing is left behind when an opener does something else.
let openingWith: NovaLandingKind = null;

export function openTaskForNova(kind: Exclude<NovaLandingKind, null>, open: () => void): void {
	openingWith = kind;
	try {
		open();
	} finally {
		openingWith = null;
	}
}

// The route state the showTaskModal helper pushes with the task's URL: the landing when the Nova
// button is opening the task, nothing otherwise.
export function novaLandingRouteState(): {[ROUTE_STATE_KEY]: Exclude<NovaLandingKind, null>} | undefined {
	return openingWith ? {[ROUTE_STATE_KEY]: openingWith} : undefined;
}

// Reads the landing back out of the route the modal was opened on. Anything other than a landing
// this module wrote, including no state at all, opens at the top.
export function novaLandingFromRouteState(state: unknown): NovaLandingKind {
	if (!state || typeof state !== 'object') return null;
	const kind = (state as Record<string, unknown>)[ROUTE_STATE_KEY];
	return kind === 'view' || kind === 'reply' ? kind : null;
}
